import type {
    DataDrivenPropertyValueSpecification,
    FillExtrusionLayerSpecification,
    FillLayerSpecification,
} from "mapbox-gl";
import type { TabData } from "./components/TabList";
import type { ExpressionSpecification } from "mapbox-gl";

export type Tab = "location" | "finance" | "property" | "rank";
export type LocationTab = "indicators" | "collections" | "attributes";
export type AttributeView = "token" | "place";
export type FinanceView = "tokens" | "places";
export type RankTarget = "tokens" | "places" | "properties";

export const TABS: TabData<Tab>[] = [
    { id: "rank", label: "Rank" },
    { id: "location", label: "Location" },
    { id: "finance", label: "Finance" },
    { id: "property", label: "Property" },
];

export const LOCATION_TABS: TabData<LocationTab>[] = [
    { id: "attributes", label: "Attributes" },
    { id: "indicators", label: "Indicators" },
    { id: "collections", label: "Collections" },
];

export const ATTRIBUTE_VIEW_TABS: TabData<AttributeView>[] = [
    { id: "token", label: "By Token" },
    { id: "place", label: "By Place" },
];

export const PLACE_LEVELS: { value: number; label: string }[] = [
    { value: 2, label: "Neighbourhood" },
    { value: 1, label: "District" },
    { value: 0, label: "Region" },
];

export const TOKEN_DISTRIBUTION_LEVEL = 16;

export const FINANCE_VIEW_TABS: TabData<FinanceView>[] = [
    { id: "tokens", label: "Tile averages" },
    { id: "places", label: "Places" },
];

export const RANK_TARGET_TABS: TabData<RankTarget>[] = [
    { id: "tokens", label: "Tokens" },
    { id: "places", label: "Places" },
    { id: "properties", label: "Properties" },
];

/** Isochrone band minutes — must match BAND_MINUTES on the tile-server. */
export const ISOCHRONE_BAND_MINUTES = [15, 25, 40, 60] as const;

/** Isochrone band colors — green→purple ramp, distinct from the cold-hot rank scale. */
export const ISOCHRONE_BAND_COLORS: Record<number, string> = {
    15: "rgba(56, 199, 132, 1)",
    25: "rgba(56, 180, 199, 1)",
    40: "rgba(96, 140, 220, 1)",
    60: "rgba(140, 110, 220, 1)",
};

export function createIsochroneColourPalette(r: number, g: number, b: number) {
    return {
        15: `rgba(${r}, ${g}, ${b}, 0.6)`,
        25: `rgba(${r}, ${g}, ${b}, 0.5)`,
        40: `rgba(${r}, ${g}, ${b}, 0.4)`,
        60: `rgba(${r}, ${g}, ${b}, 0.3)`,
    };
}

export const mapboxColdHotColourScale = [
    0,
    "rgba(120, 144, 156, 1)",
    0.5,
    "rgba(229, 201, 75, 1)",
    1,
    "rgba(255, 85, 31, 1)",
];

/** Cool→hot heatmap gradient, matching `mapboxColdHotColourScale` above (the
 * fill scale the map tiles use): 0 cool grey → 0.5 yellow → 1 hot orange. */
type Rgb = [number, number, number];
const HEAT_COOL: Rgb = [120, 144, 156];
const HEAT_MID: Rgb = [229, 201, 75];
const HEAT_HOT: Rgb = [255, 85, 31];

export function heatColor(value: number): string {
    const t = Math.max(0, Math.min(1, value));
    const [from, to, f]: [Rgb, Rgb, number] =
        t <= 0.5
            ? [HEAT_COOL, HEAT_MID, t / 0.5]
            : [HEAT_MID, HEAT_HOT, (t - 0.5) / 0.5];
    const r = Math.round(from[0] + (to[0] - from[0]) * f);
    const g = Math.round(from[1] + (to[1] - from[1]) * f);
    const b = Math.round(from[2] + (to[2] - from[2]) * f);
    return `rgb(${r}, ${g}, ${b})`;
}

// export const mapboxColdHotColourScale = [
//     0,
//     "rgba(219, 97, 50, 1)",
//     0.5,
//     "rgba(219, 97, 50, 1)",
//     1,
//     "rgba(219, 97, 50, 1)",
// ];

export function createMapboxFillAndOpacity(
    value: string[],
    expo?: number,
): FillLayerSpecification["paint"] {
    return {
        "fill-color": [
            "interpolate",
            ["linear"],
            value,
            ...mapboxColdHotColourScale,
        ],
        "fill-opacity": [
            "interpolate",
            expo ? ["exponential", expo] : ["linear"],
            value,
            0,
            0.0,
            1,
            0.5,
        ],
        "fill-outline-color": "rgba(255,255,255,0.2)",
    };
}

export const mapboxColdHotColourScaleExtrusion = [
    0,
    "rgba(219, 97, 50, 0.0)",
    0.5,
    "rgba(219, 97, 50, 1)",
    1,
    "rgba(219, 97, 50, 1)",
];

export function createMapboxExtrusion(
    value: string[],
): FillExtrusionLayerSpecification["paint"] {
    return {
        "fill-extrusion-color": [
            "interpolate",
            ["linear"],
            value,
            ...mapboxColdHotColourScaleExtrusion,
        ],
        "fill-extrusion-height": ["*", value, 500],
        "fill-extrusion-opacity": 1,
    };
}
